// CONTRACT:C2-RENDER-ENGINE.1.0
/** ANISOTROPIC ERROR-ELLIPSE plan — the pure math behind the velocity-correlated
 *  uncertainty smear (TRACKED-TASK:TODO.md#smear-motion-ellipse).
 *
 *  WHAT: today the smear (apps/cop/src/host/cloudLayer.ts GaussianCloudLayer) is an
 *  ISOTROPIC radial Gaussian — one `sigma`. A moving track's uncertainty is really
 *  DIRECTIONAL: where it'll BE (along heading) is more uncertain than where it IS
 *  (laterally). This module turns the isotropic sigma + (heading, speed, per-domain
 *  assumption) into an ELLIPSE: a cross-track sigma (unchanged) and an along-track
 *  sigma elongated by speed, rotated to the heading.
 *
 *  CHANNEL LAW (unchanged): CONFIDENCE -> extent (sigma/reach), AGE -> alpha. This only
 *  makes the EXTENT directional; it never touches alpha. The along-track stretch is a
 *  function of SPEED + a per-domain assumption, NOT of age.
 *
 *  PURE: imports nothing, deterministic, no GPU. The GLSL consumes (aspect, rotationDeg)
 *  as per-instance attributes; this is the CPU derivation feeding them. Tested directly
 *  in packages/render-engine/test/ellipse.test.ts (the updateTrigger flip/noop contract). */

/** Inputs to {@link ellipsePlan}. */
export interface EllipseInput {
  /** Confidence-derived ISOTROPIC sigma (the existing cross-track width), a fraction of reach. */
  baseSigma: number;
  /** Compass bearing of travel (deg). The ellipse long-axis aligns with this. */
  headingDeg: number;
  /** Current ground speed (knots). Drives the along-track elongation. */
  speedKn: number;
  /** Per-domain along-track elongation factor at full speed (ships small, unknown-air large).
   *  See apps/cop/src/host/renderSettingsKeys.ts EllipseSettings. */
  domainStretch: number;
  /** Speed (knots) at which the elongation saturates. Default 600 (so air saturates, ships barely). */
  speedRefKn?: number;
  /** Hard cap on the along/cross aspect ratio so a cloud never becomes a needle. Default 2.6. */
  maxAspect?: number;
}

/** The derived ellipse. `aspect` = sigmaAlong / sigmaCross (>= 1). */
export interface EllipsePlan {
  /** Cross-track Gaussian sigma (= baseSigma; the lateral "where it is" uncertainty). */
  sigmaCross: number;
  /** Along-track Gaussian sigma (>= sigmaCross; the "where it'll be" uncertainty). */
  sigmaAlong: number;
  /** Along/cross aspect ratio, clamped to [1, maxAspect]. 1 = isotropic (static / slow). */
  aspect: number;
  /** Ellipse long-axis bearing (deg), normalized to [0, 360). */
  rotationDeg: number;
}

const DEFAULT_SPEED_REF_KN = 600;
const DEFAULT_MAX_ASPECT = 2.6;

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
function wrapDeg(d: number): number {
  return ((d % 360) + 360) % 360;
}

/** Derive the anisotropic ellipse for a track. PURE + total:
 *  - a STATIC / zero-speed track (or zero domainStretch) is ISOTROPIC (aspect === 1) — identical
 *    to today's radial Gaussian, so the change is a strict superset (no regression at rest).
 *  - a MOVING track stretches ALONG heading: aspect = clamp(1 + domainStretch * speedNorm, 1, max),
 *    where speedNorm = clamp(speed / speedRef, 0, 1). Higher domainStretch (unknown/hostile air)
 *    elongates more than a tight ship at the same speed. */
export function ellipsePlan(input: EllipseInput): EllipsePlan {
  const baseSigma = Number.isFinite(input.baseSigma) ? Math.max(0, input.baseSigma) : 0;
  const speedRef = input.speedRefKn && input.speedRefKn > 0 ? input.speedRefKn : DEFAULT_SPEED_REF_KN;
  const maxAspect = input.maxAspect && input.maxAspect >= 1 ? input.maxAspect : DEFAULT_MAX_ASPECT;
  const speed = Number.isFinite(input.speedKn) ? Math.max(0, input.speedKn) : 0;
  const stretch = Number.isFinite(input.domainStretch) ? Math.max(0, input.domainStretch) : 0;

  const speedNorm = clamp(speed / speedRef, 0, 1);
  const aspect = clamp(1 + stretch * speedNorm, 1, maxAspect);
  // A non-finite heading (bad feed) must not poison the trigger — fall back to 0 (north).
  const heading = Number.isFinite(input.headingDeg) ? input.headingDeg : 0;
  return {
    sigmaCross: baseSigma,
    sigmaAlong: baseSigma * aspect,
    aspect,
    rotationDeg: wrapDeg(heading),
  };
}

/** A stable string fingerprint of an {@link EllipsePlan} for deck.gl `updateTriggers`.
 *  It FLIPS when the ellipse geometry changes (aspect / rotation / cross-sigma) and is a
 *  NO-OP (byte-identical) when those are unchanged — so the smear layer re-keys its
 *  per-instance attributes only when they actually moved, never every frame. Quantized so
 *  sub-perceptual jitter (e.g. a 0.01° heading wobble) does not thrash the trigger. */
export function ellipseTriggerKey(plan: EllipsePlan): string {
  return `${plan.aspect.toFixed(3)}|${plan.rotationDeg.toFixed(1)}|${plan.sigmaCross.toFixed(3)}`;
}
